import Joi from 'joi'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../UI/Btn'
import TextAreaField from '../../../../UI/TextAreaField'
import BaseModal from '../../../../UI/BaseModal/BaseModal'

import http from '../../../../shared/http'
import { TApi, TReject } from '../../../../shared/const'
import { schemaOptions } from '../../../../shared/i18n'
import { types } from '../../../../shared/eventEmitter'

import styles from './style.module.scss'

const schema = Joi.object({
	description: Joi.string().max(500).required()
})

const CLOSING = 'Herzliche Grüße,\nJunior Achievement Austria Team'

// label -> full snippet text inserted into the message body
const PARENTAL_SNIPPETS = [
	{
		label: 'Name fehlt',
		text: 'Bitte trage deinen vollständigen Namen (Vor- und Nachname) im obersten Feld ein. Das Dokument ist nur mit deinem Namen gültig.'
	},
	{
		label: 'Unterschrift fehlt',
		text: 'Bitte lasse das Dokument von einer erziehungsberechtigten Person unterschreiben.'
	},
	{
		label: 'Name Erziehungsberechtigte:r',
		text: 'Bitte trage den Namen der unterzeichnenden erziehungsberechtigten Person leserlich ein.'
	},
	{
		label: 'Bereits volljährig',
		text: 'Da du laut Geburtsdatum bereits volljährig bist: Trage oben deinen Namen ein, lasse das Feld für die erziehungsberechtigte Person leer und unterschreibe selbst.'
	},
	{
		label: 'Unvollständig',
		text: 'Bitte fülle die Erklärung vollständig aus.'
	},
	{
		label: 'Schlecht lesbar',
		text: 'Bitte fotografiere das Dokument gut lesbar neu und lade es erneut hoch.'
	},
	{
		label: 'Datei öffnet nicht',
		text: 'Die Datei lässt sich nicht öffnen. Bitte lade sie als PDF oder JPEG erneut hoch.'
	},
	{
		label: 'Falsches Dokument',
		text: 'Bitte lade die korrekte Einverständniserklärung hoch.'
	},
	{
		label: 'Geburtsdatum nachtragen',
		text: 'Bitte trage zusätzlich dein Geburtsdatum in der Datenbank ein.'
	}
]

// true when the reject type pre-fills a gender-aware greeting + closing and offers snippets
const isStudentReject = reject => reject === TReject.PARENTAL

// Gender-aware greeting. Handles 'FEMALE'/'MALE'/'DIVERS' and
// 'Weiblich'/'Männlich'/'Divers' case-insensitively.
const buildGreeting = (firstName, gender) => {
	if (!firstName) return 'Liebe/r Schüler/in,'

	const g = (gender || '').toString().trim().toLowerCase()
	const isFemale = g.startsWith('f') || g.startsWith('weib')
	const isMale = g.startsWith('m') || g.startsWith('männ') || g.startsWith('mann')

	if (isFemale) return `Liebe ${firstName},`
	if (isMale) return `Lieber ${firstName},`
	return `Liebe/r ${firstName},`
}

const composeMessage = (greeting, selectedTexts) => {
	const body = selectedTexts.join('\n\n')
	return `${greeting}\n\n${body ? `${body}\n\n` : ''}${CLOSING}`
}

export default function RejectCompanyComment() {
	const { t } = useTranslation()
	const titles = {
		[TReject.PARENTAL]: t('Reason for rejecting the parental confirmation'),
		[TReject.COMPANY]: t('Reason for rejection to the entire team')
	}
	const {
		reset,
		register,
		setValue,
		handleSubmit,
		formState: { errors, dirtyFields }
	} = useForm({
		resolver: joiResolver(schema, {
			errors: {
				labels: false,
				language: localStorage.getItem('i18nextLng')
			},
			...schemaOptions
		})
	})

	const [id, setId] = useState(null)
	const [isLoading, setLoading] = useState(false)
	const [reject, setReject] = useState('')
	const [onDone, setOnDone] = useState(null)
	const [greeting, setGreeting] = useState('')
	const [selected, setSelected] = useState([])
	const [descriptionValue, setDescriptionValue] = useState('')

	const handleOpen = (id, reject, onDone, student = {}) => {
		setOnDone(() => onDone)
		setId(id)
		setReject(reject)
		setSelected([])
		reset()

		if (isStudentReject(reject)) {
			const newGreeting = buildGreeting(student.firstName, student.gender)
			setGreeting(newGreeting)
			const composed = composeMessage(newGreeting, [])
			setValue('description', composed)
			setDescriptionValue(composed)
		} else {
			setGreeting('')
			setValue('description', '')
			setDescriptionValue('')
		}
	}

	const toggleSnippet = label => {
		const nextSelected = selected.includes(label)
			? selected.filter(l => l !== label)
			: [...selected, label]
		setSelected(nextSelected)

		// keep original snippet order so re-checking is consistent
		const selectedTexts = PARENTAL_SNIPPETS
			.filter(s => nextSelected.includes(s.label))
			.map(s => s.text)
		const composed = composeMessage(greeting, selectedTexts)
		setValue('description', composed)
		setDescriptionValue(composed)
	}

	const resetMessage = () => {
		setSelected([])
		const composed = composeMessage(greeting, [])
		setValue('description', composed)
		setDescriptionValue(composed)
	}

	const onSubmit = (fields, closeModal) => {
		setLoading(true)

		switch (reject) {
			case TReject.COMPANY: {
				http.put(TApi.ADMIN_PUT_COMPANY_REJECT(id), fields)
					.then(() => {
						toast.success(t('The company was rejected successfully'))
						closeModal()
						reset()
						setId(null)
						if (onDone) onDone()
					})
					.finally(() => {
						setLoading(false)
					})
				break
			}
			case TReject.PARENTAL: {
				http.post(TApi.ADMIN_REJECT_PARENTAL_CONFIRMATION(id), fields)
					.then(() => {
						toast.success(t('The parental confirmation was rejected successfully'))
						closeModal()
						reset()
						setId(null)
						if (onDone) onDone()
					})
					.finally(() => {
						setLoading(false)
					})
				break
			}
			default: {
				break
			}
		}
	}

	return (
		<BaseModal
			onOpen={handleOpen}
			nameEvent={types.openRejectCompanyCommentModal}
			isDirty={Boolean(Object.keys(dirtyFields).length)}
			title={titles[reject]}
			maxWidth={720}>
			{({ closeModal }) => (
				<form className={styles.root} onSubmit={handleSubmit(form => onSubmit(form, closeModal))}>
					{isStudentReject(reject) && (
						<div className={styles.snippets}>
							{PARENTAL_SNIPPETS.map(({ label }) => {
								const active = selected.includes(label)
								return (
									<button
										key={label}
										type="button"
										className={`${styles.snippet} ${active ? styles.snippetActive : ''}`}
										aria-pressed={active}
										onClick={() => toggleSnippet(label)}>
										<span className={styles.snippetCheck}>{active ? '✓' : '+'}</span>
										{label}
									</button>
								)
							})}
						</div>
					)}

					<TextAreaField
						mb={25}
						label={t('description')}
						height={240}
						maxLength={500}
						showLettersLeft
						value={descriptionValue}
						register={register('description')}
						error={errors.description}
						placeholder={t('description')}
					/>

					<div className={styles.actions}>
						{isStudentReject(reject) && (
							<button type="button" className={styles.resetLink} onClick={resetMessage}>
								<svg className={styles.resetIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
							Inhalt zurücksetzen
							</button>
						)}
						<Btn
							loading={isLoading}
							className={styles.btnSubmit}
							type="submit"
							icon="send"
							iconSize={{ w: 15, h: 15 }}
							iconColor="#fff"
							iconClassName={styles.icon}>
							{reject === TReject.COMPANY ? t('Send to the team') : t('Send to student')}
						</Btn>
					</div>
				</form>
			)}
		</BaseModal>
	)
}
